import { createRoute, Link, useSearch } from "@tanstack/solid-router";
import { createSignal, Match, onMount, Switch } from "solid-js";
import { authRoute } from "./route";
import { api } from "@/lib/api";
import { sharedApi } from "@agape/shared";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "@fleet/icons";
import { AuthScene } from "@/components/auth/auth-scene";

/**
 * Email verification page: consumes the token from the emailed link
 * (`/verify-email?token=…`) through the API and displays the result.
 * The link expires after 24 h; in that case, resending is offered from the profile.
 */
function VerifyEmailPage() {
  const search = useSearch({ from: "/_auth/verify-email" });
  const { t } = useI18n();
  const [status, setStatus] = createSignal<"loading" | "success" | "error">("loading");

  onMount(() => {
    void (async () => {
      const { token } = search();
      if (!token) {
        setStatus("error");
        return;
      }
      try {
        const res = await sharedApi.verifyEmail(api, { token });
        setStatus(res.error ? "error" : "success");
      } catch {
        setStatus("error");
      }
    })();
  });

  return (
    <AuthScene
      headline={t(msg`Verifying your email.`)}
      tagline={t(msg`Hang tight while we confirm your email address.`)}
      footer={
        <p class="text-sm text-muted-foreground">
          <Link to="/login" class="font-medium text-primary underline-offset-4 hover:underline">
            {t(msg`Back to sign in`)}
          </Link>
        </p>
      }
    >
      <div class="space-y-5">
        <Switch>
          <Match when={status() === "loading"}>
            <div
              class="flex items-center justify-center gap-3 p-4 text-sm text-muted-foreground"
              data-testid="verify-loading"
            >
              <Loader2 class="h-5 w-5 animate-spin" />
              {t(msg`Verifying your email…`)}
            </div>
          </Match>
          <Match when={status() === "success"}>
            <div
              class="space-y-1 rounded-xl border border-primary/30 bg-primary/5 p-4 text-center text-sm"
              data-testid="verify-success"
            >
              <CheckCircle2 class="mx-auto h-8 w-8 text-primary" />
              <p class="font-medium">{t(msg`Email verified!`)}</p>
              <p class="text-muted-foreground">
                {t(msg`Your email address has been confirmed. You can now continue using the app.`)}
              </p>
            </div>
          </Match>
          <Match when={status() === "error"}>
            <div
              class="space-y-1 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-center text-sm"
              data-testid="verify-error"
            >
              <XCircle class="mx-auto h-8 w-8 text-destructive" />
              <p class="font-medium text-destructive">{t(msg`Invalid link`)}</p>
              <p class="text-muted-foreground">
                {t(
                  msg`This verification link is invalid or has expired. You can request a new one from your profile.`,
                )}
              </p>
            </div>
          </Match>
        </Switch>

        <Link to="/" class="block">
          <Button class="w-full">{t(msg`Go to the app`)}</Button>
        </Link>
      </div>
    </AuthScene>
  );
}

export const verifyEmailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/verify-email",
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: VerifyEmailPage,
});
